import * as React from 'react';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { useNavigate } from 'react-router';
import { getMany as getOrders, type Order, type OrderStatus } from '../data/orders';
import { useLanguage } from '../../contexts/LanguageContext';
import dayjs from 'dayjs';
import DateRangeFilter from './DateRangeFilter';
import CustomTablePagination from './CustomTablePagination';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

interface EmployeeOrdersTableProps {
  userId: number;
}

export default function EmployeeOrdersTable({ userId }: EmployeeOrdersTableProps) {
  const navigate = useNavigate();
  const { t, direction } = useLanguage();
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = React.useState(0);
  const [dateFrom, setDateFrom] = React.useState<string | null>(null);
  const [dateTo, setDateTo] = React.useState<string | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<OrderStatus | ''>('');
  const [mobileNumber, setMobileNumber] = React.useState('');
  const [customerName, setCustomerName] = React.useState('');
  const [columnMenuAnchor, setColumnMenuAnchor] = React.useState<null | HTMLElement>(null);

  // Default column visibility - specify which columns to hide by default
  const defaultColumnVisibility: GridColumnVisibilityModel = {
    id: false,              // Hidden by default
    customerNumber: false,  // Hidden by default
    people_number: false,   // Hidden by default
    paid_amount: false,     // Hidden by default
    created_at: false,      // Hidden by default
  };

  // Column visibility state - load from localStorage or use defaults
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>(() => {
    const saved = localStorage.getItem('employeeOrdersColumnVisibility');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Merge with defaults to ensure all columns have explicit values
        return { ...defaultColumnVisibility, ...parsed };
      } catch {
        return defaultColumnVisibility;
      }
    }
    return defaultColumnVisibility;
  });

  // Save column visibility to localStorage whenever it changes
  const handleColumnVisibilityModelChange = React.useCallback(
    (newModel: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(newModel);
      localStorage.setItem('employeeOrdersColumnVisibility', JSON.stringify(newModel));
    },
    [],
  );

  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await getOrders({
        paginationModel,
        sortModel: [{ field: 'created_at', sort: 'desc' }],
        filterModel: { items: [] },
        userId,
        sortBy: 'created_at',
        sortOrder: 'desc',
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        status: statusFilter || undefined,
        mobileNumber: mobileNumber || undefined,
        customerName: customerName || undefined,
      });

      setOrders(result.items);
      setRowCount(result.itemCount);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, paginationModel, dateFrom, dateTo, statusFilter, mobileNumber, customerName]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CREATED':
        return '#ff9800';
      case 'BOOKED':
        return '#4caf50';
      case 'CANCELED':
        return '#f44336';
      case 'COMPLETED':
        return '#2196f3';
      default:
        return '#757575';
    }
  };

  const formatAmount = (amount: string | number | undefined) => {
    if (!amount) return '0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return numAmount.toFixed(2);
  };

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'id',
        headerName: t('orders.id'),
        width: 60,
        minWidth: 50,
        flex: 0.4,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'customer',
        headerName: t('orders.customer'),
        valueGetter: (value: { name?: string } | null | undefined) => value?.name || '',
        width: 120,
        minWidth: 100,
        flex: 0.9,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'customerNumber',
        headerName: t('orders.customerNumber'),
        valueGetter: (value, row) => (row.customer?.mobile_number || '').replace(/\s+/g, ''),
        renderCell: (params) => <span dir="ltr">{params.value}</span>,
        width: 110,
        minWidth: 100,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'people_number',
        headerName: t('orders.people_number'),
        width: 80,
        minWidth: 70,
        flex: 0.5,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'destination',
        headerName: t('orders.destination'),
        width: 100,
        minWidth: 80,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },
      {
        field: 'full_amount',
        headerName: t('orders.totalAmount'),
        width: 110,
        minWidth: 100,
        flex: 0.7,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        valueGetter: (value, row: Order) => {
          const amount = typeof row.full_amount === 'string'
            ? parseFloat(row.full_amount)
            : row.full_amount;
          return amount || 0;
        },
        renderCell: (params) => {
          const currencySymbol = direction === 'rtl' ? 'د.إ' : 'AED';
          return `${formatAmount(params.value)} ${currencySymbol}`;
        },
      },
      {
        field: 'paid_amount',
        headerName: t('orders.paidAmount'),
        width: 110,
        minWidth: 100,
        flex: 0.7,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        valueGetter: (value, row: Order) => {
          const amount = typeof row.paid_amount === 'string'
            ? parseFloat(row.paid_amount)
            : row.paid_amount;
          return amount || 0;
        },
        renderCell: (params) => {
          const currencySymbol = direction === 'rtl' ? 'د.إ' : 'AED';
          return `${formatAmount(params.value)} ${currencySymbol}`;
        },
      },
      {
        field: 'pending_amount',
        headerName: t('orders.pendingAmount'),
        width: 110,
        minWidth: 100,
        flex: 0.7,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        valueGetter: (value, row: Order) => {
          const amount = typeof row.pending_amount === 'string'
            ? parseFloat(row.pending_amount)
            : row.pending_amount;
          return amount || 0;
        },
        renderCell: (params) => {
          const currencySymbol = direction === 'rtl' ? 'د.إ' : 'AED';
          return `${formatAmount(params.value)} ${currencySymbol}`;
        },
      },
      {
        field: 'status',
        headerName: t('orders.status'),
        width: 110,
        minWidth: 100,
        flex: 0.7,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: (params) => (
          <Chip
            label={t(`orders.${params.value.toLowerCase()}`)}
            size="small"
            sx={{
              backgroundColor: getStatusColor(params.value),
              color: 'white',
              fontWeight: 600,
              minWidth: 90,
            }}
          />
        ),
      },
      {
        field: 'created_at',
        headerName: t('orders.createdAt'),
        width: 120,
        minWidth: 110,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        valueGetter: (value, row: Order) =>
          dayjs(row.created_at).format('YYYY-MM-DD'),
      },
    ],
    [t, direction],
  );

  const handleRowClick = React.useCallback(
    (params: any) => {
      navigate(`/orders/${params.id}`, {
        state: { from: `/employees/${userId}`, tab: 1 }
      });
    },
    [navigate, userId],
  );

  const handleDateFromChange = React.useCallback((date: string | null) => {
    setDateFrom(date);
  }, []);

  const handleDateToChange = React.useCallback((date: string | null) => {
    setDateTo(date);
  }, []);

  const handleStatusFilterChange = React.useCallback((status: OrderStatus | '') => {
    setStatusFilter(status);
  }, []);

  const handleColumnMenuOpen = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  }, []);

  const handleColumnMenuClose = React.useCallback(() => {
    setColumnMenuAnchor(null);
  }, []);

  const handleToggleColumn = React.useCallback(
    (field: string) => {
      setColumnVisibilityModel((prev) => {
        const currentValue = prev[field] ?? true; // Default to true if undefined
        const newModel = {
          ...prev,
          [field]: !currentValue,
        };
        localStorage.setItem('employeeOrdersColumnVisibility', JSON.stringify(newModel));
        return newModel;
      });
    },
    [],
  );

  // Define available columns for visibility toggle
  const availableColumns = React.useMemo(() => [
    { field: 'id', label: t('orders.id') },
    { field: 'customer', label: t('orders.customer') },
    { field: 'customerNumber', label: t('orders.customerNumber') },
    { field: 'people_number', label: t('orders.people_number') },
    { field: 'destination', label: t('orders.destination') },
    { field: 'full_amount', label: t('orders.totalAmount') },
    { field: 'paid_amount', label: t('orders.paidAmount') },
    { field: 'pending_amount', label: t('orders.pendingAmount') },
    { field: 'status', label: t('orders.status') },
    { field: 'created_at', label: t('orders.createdAt') },
  ], [t]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Toggle Columns" placement="bottom" enterDelay={1000}>
            <IconButton
                size="small"
                aria-label="toggle columns"
                onClick={handleColumnMenuOpen}
            >
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Box
        sx={{
          flex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={t('orders.customer')}
            value={customerName}
            onChange={(e) => { setCustomerName(e.target.value); setPaginationModel((p) => ({ ...p, page: 0 })); }}
            sx={{ minWidth: 140 }}
          />
          <TextField
            size="small"
            placeholder={t('orders.customerNumber')}
            value={mobileNumber}
            onChange={(e) => { setMobileNumber(e.target.value); setPaginationModel((p) => ({ ...p, page: 0 })); }}
            sx={{ minWidth: 140 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value as OrderStatus | '')}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: '#999' }}>{t('orders.status')}</span>;
                }
                return t(`orders.${selected.toLowerCase()}`);
              }}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="CREATED">{t('orders.created')}</MenuItem>
              <MenuItem value="BOOKED">{t('orders.booked')}</MenuItem>
              <MenuItem value="CANCELED">{t('orders.canceled')}</MenuItem>
              <MenuItem value="COMPLETED">{t('orders.completed')}</MenuItem>
            </Select>
          </FormControl>
          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={handleDateFromChange}
            onDateToChange={handleDateToChange}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <DataGrid
              rows={orders}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
              paginationMode="server"
              pageSizeOptions={[25, 50, 75, 100]}
              rowCount={rowCount}
              loading={isLoading}
              onRowClick={handleRowClick}
              hideFooter
              sx={{
                height: '100%',
                width: '100%',
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(2, 139, 203, 0.05)',
                  },
                },
              }}
              disableRowSelectionOnClick
              localeText={{
                noRowsLabel: t('orders.noOrders') || 'No orders found',
                noResultsOverlayLabel: t('orders.noOrders') || 'No orders found',
              }}
            />
          </Box>
          <CustomTablePagination
            paginationModel={paginationModel}
            rowCount={rowCount}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 75, 100]}
          />
        </Box>
      </Box>

      {/* Column Visibility Popover */}
      <Popover
        open={Boolean(columnMenuAnchor)}
        anchorEl={columnMenuAnchor}
        onClose={handleColumnMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: direction === 'rtl' ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: direction === 'rtl' ? 'left' : 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Show/Hide Columns
          </Typography>
          <Stack spacing={0.5}>
            {availableColumns.map((col) => (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox
                    checked={columnVisibilityModel[col.field] ?? true}
                    onChange={() => handleToggleColumn(col.field)}
                    size="small"
                  />
                }
                label={col.label}
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}

